import React from 'react';
import { IAuditoriaCircularidadeTransitoriaResultado } from '../interfaces/IAuditoriaCircularidadeTransitoria';

interface Props {
  resultado: IAuditoriaCircularidadeTransitoriaResultado;
  onVerDetalhe: () => void;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  const cls = tipo === 'GIRO_CIRCULAR' ? 'badge-light-danger' : 'badge-light-warning';
  const label = tipo === 'GIRO_CIRCULAR' ? 'Giro Circular' : 'Saldo Aberto';
  return <span className={`badge ${cls}`}>{label}</span>;
};

const AuditoriaResumoCircularidadeTransitoria: React.FC<Props> = ({ resultado, onVerDetalhe }) => {
  if (resultado.status === 'CONCILIADO' && resultado.itens.length === 0) return null;

  // COR-250: ordenar pela base de cálculo (BC) real de cada ocorrência — para SALDO_ABERTO é o
  // saldo final da ECD, não o movimento do período (uma conta pode ter saldo em aberto relevante
  // com pouco ou nenhum movimento no período, se o saldo veio majoritariamente do período anterior).
  const bcDaOcorrencia = (item: (typeof resultado.itens)[number]) =>
    item.tipoOcorrencia === 'GIRO_CIRCULAR' ? item.totalMovimento / 2 : item.saldoFinalEcd;

  const top5 = [...resultado.itens]
    .sort((a, b) => bcDaOcorrencia(b) - bcDaOcorrencia(a))
    .slice(0, 5);

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Circularidade de Fluxo em Contas Transitórias Inter-company
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            RIR/2018 Art. 579 · Lei 9.430/96 Art. 3º e 44 I · Lei 6.404/76 Art. 247 · CPC 15 (R1)
          </span>
        </h3>
        <div className='card-toolbar'>
          <button className='btn btn-sm btn-light-primary' onClick={onVerDetalhe}>
            Ver Detalhes
          </button>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='row mb-5'>
          <div className='col-3 text-center border-end'>
            <div className='fs-2 fw-bolder text-danger'>{resultado.totalErros}</div>
            <div className='text-muted fs-8'>Erros</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-2 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted fs-8'>Alertas</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-2 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted fs-8'>Avisos</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-2 fw-bolder text-success'>
              {formatCurrency(resultado.passivoTributarioTotal)}
            </div>
            <div className='text-muted fs-8'>Passivo Confirmado</div>
          </div>
        </div>

        {/* COR-256: passivo confirmado é sempre R$ 0,00 — StatusDocumentacao é sempre
            "NAO_ANALISADO" (nenhuma confirmação documental disponível), então a auditoria nunca
            conclui ERRO nem passivo fiscal confirmado automaticamente. A referência informativa
            (COR-257/258, juros presumidos sobre o saldo constituído no período, nunca sobre o
            principal nem saldo herdado) é exibida separadamente, nunca somada ao passivo acima. */}
        {resultado.passivoReferenciaTotal > 0 && (
          <div className='alert alert-light-primary border border-primary p-3 fs-8 mb-5'>
            <strong>Referência informativa (não confirmada):</strong> juros presumidos sobre o saldo
            constituído no período (RIR/2018 Art. 579) — nunca sobre o principal do mútuo nem sobre
            saldo herdado de exercícios anteriores: <strong>{formatCurrency(resultado.passivoReferenciaTotal)}</strong>.
            Pendente de validação documental do contrato de mútuo, taxa de juros e substância econômica.
          </div>
        )}

        {/* COR-201: separação por tipo de ocorrência — evita que o usuário confunda
            circularidade (giro bidirecional) com saldo em aberto (conta não zerada). */}
        <div className='row mb-5'>
          <div className='col-6 text-center border-end'>
            <div className='fs-3 fw-bolder text-danger'>{resultado.totalGiroCircular}</div>
            <div className='text-muted fs-8'>Ocorrência(s) — Giro Circular</div>
          </div>
          <div className='col-6 text-center'>
            <div className='fs-3 fw-bolder text-warning'>{resultado.totalSaldoAberto}</div>
            <div className='text-muted fs-8'>Ocorrência(s) — Saldo em Aberto</div>
          </div>
        </div>

        {top5.length > 0 && (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Conta</th>
                  <th className='text-center'>Tipo</th>
                  <th className='text-end'>Movimento Total</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {top5.map((item, i) => (
                  <tr key={i}>
                    <td>
                      <span className='fw-bold'>{item.empresaNome}</span>
                      <br />
                      <span className='text-muted fs-8'>{item.cnpj}</span>
                    </td>
                    <td>
                      <span className='fw-bold fs-8'>{item.contaReferencial}</span>
                      <br />
                      <span className='text-muted fs-8'>{item.descricaoConta}</span>
                    </td>
                    <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
                    <td className='text-end fw-bold'>{formatCurrency(item.totalMovimento)}</td>
                    <td className='text-center'><StatusBadge status={item.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaResumoCircularidadeTransitoria;
